/**
 * In-memory table store.
 *
 * Rows are copied out of the snapshot on first touch, so inserts/updates/
 * deletes mutate this session's copy only. Reloading the page rebuilds
 * everything from the snapshot — that reset is intentional.
 */
import type { DemoTableLoader, FakeSupabaseOptions, Row } from './types';

type DemoDataModule = {
  loadTable?: (name: string) => Promise<Row[]>;
  DEMO_TABLE_NAMES?: readonly string[];
};

// Resolved lazily and only if `src/demo/data/index.ts` exists. `import.meta.glob`
// is the one import form that compiles to `{}` instead of a hard error when the
// module is absent, which keeps this file buildable while that module is being
// written in parallel.
const dataModules = import.meta.glob('../data/index.{ts,tsx}') as Record<
  string,
  () => Promise<unknown>
>;

let modulePromise: Promise<DemoDataModule | null> | null = null;

function demoDataModule(): Promise<DemoDataModule | null> {
  if (!modulePromise) {
    const key = Object.keys(dataModules).sort()[0];
    modulePromise = key
      ? dataModules[key]!()
        .then((m) => (m ?? null) as DemoDataModule | null)
        .catch(() => null)
      : Promise.resolve(null);
  }
  return modulePromise;
}

export class DemoStore {
  private readonly cache = new Map<string, Row[]>();

  private readonly inflight = new Map<string, Promise<Row[]>>();

  private readonly loader: DemoTableLoader | null;

  private readonly known: ReadonlySet<string> | null;

  /** Memo for inferred embed relationships, keyed `parent>child`. */
  readonly relationCache = new Map<string, unknown>();

  constructor(opts: FakeSupabaseOptions = {}) {
    this.loader = opts.loadTable ?? null;
    this.known = opts.tableNames ? new Set(opts.tableNames) : null;
    if (opts.tables) {
      for (const [name, rows] of Object.entries(opts.tables)) {
        this.cache.set(name, rows.map((r) => ({ ...r })));
      }
    }
  }

  /**
   * The live, mutable row array for `name`. Unknown tables resolve to an empty
   * array (never an error) — the app queries plenty of tables the snapshot does
   * not carry, and a demo should render an empty card, not a crash.
   */
  table(name: string): Promise<Row[]> {
    const cached = this.cache.get(name);
    if (cached) return Promise.resolve(cached);
    const pending = this.inflight.get(name);
    if (pending) return pending;

    const p = this.load(name).then((rows) => {
      const existing = this.cache.get(name);
      if (existing) return existing;
      this.cache.set(name, rows);
      this.inflight.delete(name);
      return rows;
    });
    this.inflight.set(name, p);
    return p;
  }

  private async load(name: string): Promise<Row[]> {
    if (this.known && !this.known.has(name)) return [];
    try {
      if (this.loader) {
        const rows = await this.loader(name);
        return Array.isArray(rows) ? rows.map((r) => ({ ...r })) : [];
      }
      const mod = await demoDataModule();
      if (!mod || typeof mod.loadTable !== 'function') return [];
      if (Array.isArray(mod.DEMO_TABLE_NAMES) && !mod.DEMO_TABLE_NAMES.includes(name)) return [];
      const rows = await mod.loadTable(name);
      return Array.isArray(rows) ? rows.map((r) => ({ ...r })) : [];
    } catch {
      return [];
    }
  }
}
